import { observer } from "mobx-react";
import { SingleValue } from "react-select";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { Input } from "../../../../../components/inputs/input/input";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import Card from "../../../../../components/layouts/card/card";
import ListLayout from "../../../../../components/layouts/list-layout/list-layout";
import { FEATURES } from "../../../../../models/Plan";
import { ORDER_DELIVERY_FALLBACK, ORDER_DELIVERY_FALLBACK_MAP } from "../../../../../models/User";
import currentPortalStore from "../../../../../stores/current-portal-store";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import currentAccountStore from "../../../../accounts/stores/current-account-store";
import {Toggle} from "../../../../../components/inputs/toggle/toggle";

interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    dataName?: string; /** used by Candu to target an element */
}

const TrackingFallbackCard: React.FC<IProps> = observer(({ onExpandClick, isExpanded, dataName }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

    const storeSetting = currentPortalStore.setting;
    const returnLabelFeePaymentValue = storeSetting?.orderDeliveryFallback ? {
        label: getTranslation(ORDER_DELIVERY_FALLBACK_MAP[storeSetting.orderDeliveryFallback].label, pageScope),
        value: storeSetting.orderDeliveryFallback
    } : undefined;

    const onChangeOrderDeliveryFallbackValue = (value: SingleValue<IOption>) => {
        storeSetting?.setOrderDeliveryFallback(value?.value as ORDER_DELIVERY_FALLBACK);
    }

    const orderShipmentInTransitFallbackValue = storeSetting?.shipmentInTransitFallback !== undefined ? {
        label: storeSetting.shipmentInTransitFallback === 0
            ? getTranslation('UPDATE_BY_TRACKING', pageScope)
            : storeSetting.shipmentInTransitFallback + '',
        value: storeSetting.shipmentInTransitFallback
    } : undefined;

    const orderShipmentInTransitFallbackOptions = [{
        label: getTranslation('UPDATE_BY_TRACKING', pageScope),
        value: 0
    }];

    const MAX_DAY_PASSED_SINCE_FIRST_IN_TRANSIT_UPDATE = 60;
    for (let i = 1; i <= MAX_DAY_PASSED_SINCE_FIRST_IN_TRANSIT_UPDATE; i++) {
        orderShipmentInTransitFallbackOptions.push({
            label: `${i}`,
            value: i
        });
    }

    const onOrderShipmentInTransitFallback = (value: SingleValue<IOption>) => {
        storeSetting?.setShipmentInTransitFallback(Number(value?.value));
    }

    return <Card
        toolbar={{
            title: (
                <FontBody1 variant={FONT_VARIANT.BOLD}>
                    <DashTranslation translationKey="TRACKING_FALLBACK" scope={pageScope} />
                </FontBody1>
            )
        }}
        expandCallback={onExpandClick}
        isExpanded={isExpanded}
        dataName={dataName}
    >
        {currentAccountStore.isSysAdmin &&
            <FormFieldLine
                label={getTranslation('STRICT_DELIVERY_DATE', pageScope)}
                note={''}
                dataName="strict-delivery-date"
            >
                <Toggle label={''}
                        checked={storeSetting?.isStrictDeliveryDate}
                        onChange={(v) => storeSetting?.setIsStrictDeliveryDate(v)}
                        disabled={false}
                />
            </FormFieldLine>
        }
        <FormFieldLine
            label={getTranslation('REFER_TO_ORDER_AS_DELIVERED_WHEN', pageScope)}
            note={getTranslation('WILL_BE_USED_TO_CHECK_ORDER_ELIGIBLE_FOR_ORDERS_DELIVERED_CONDITION', pageScope)}
            dataName="refer-to-order-as-delivered-when"
        >
            <ListLayout>
                <SelectDropdown
                    value={returnLabelFeePaymentValue}
                    onChange={onChangeOrderDeliveryFallbackValue}
                    options={[
                        {
                            label: getTranslation(ORDER_DELIVERY_FALLBACK_MAP[ORDER_DELIVERY_FALLBACK.DEFAULT].label, pageScope),
                            value: ORDER_DELIVERY_FALLBACK.DEFAULT
                        },
                        {
                            label: getTranslation(ORDER_DELIVERY_FALLBACK_MAP[ORDER_DELIVERY_FALLBACK.DAYS_SINCE_FULFILLMENT].label, pageScope),
                            value: ORDER_DELIVERY_FALLBACK.DAYS_SINCE_FULFILLMENT
                        }
                    ]}
                />
                {storeSetting?.orderDeliveryFallback === ORDER_DELIVERY_FALLBACK.DAYS_SINCE_FULFILLMENT && <Input
                    required
                    type='number'
                    max={9999}
                    value={storeSetting?.orderDeliveryFallbackDays}
                    onChange={storeSetting?.setOrderDeliveryFallbackDays}
                />}
            </ListLayout>
        </FormFieldLine>
        {(storeSetting && currentPortalStore.isFeatureAllowed(FEATURES.AUTO_EXCHANGE)) &&
            (
                <FormFieldLine
                    label={getTranslation('REFER_TO_SHIPMENTS_AS_DELIVERED_WHEN', pageScope)}
                    note={getTranslation('THIS_WILL_BE_USED_FOR_AUTOMATION_WHEN_TRIGGER_SHIPMENT_STATUS', pageScope)}
                    dataName="refer-to-shipments-as-delivered-when"
                >
                    <div>
                        <SelectDropdown
                            options={orderShipmentInTransitFallbackOptions}
                            value={orderShipmentInTransitFallbackValue}
                            onChange={onOrderShipmentInTransitFallback}
                            isSearchable={true}
                        />
                        {storeSetting?.shipmentInTransitFallback !== 0 && <FontBody1 color={FONT_COLOR.NEUTRAL3}>
                            <DashTranslation translationKey="DAYS_PAST_SINCE_FIRST_IN_TRANSIT_UPDATE" scope={pageScope} />
                        </FontBody1>}
                    </div>

                </FormFieldLine>
            )}
    </Card>
});

export default TrackingFallbackCard;